import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import {
  LowStockWarningModel,
  NonSerializedAdjustmentNoteModel,
  NonSerializedInventoryModel,
  NonSerializedInventoryTransferModel,
} from 'src/app/shared/models';
import {
  NonSerializedInventoryGRPCService,
} from 'src/app/ProxyServices/non-serialized-inventory.grpc.service';
import { Observable } from 'rxjs';
import { ONotificationService } from '@olameter/o-utilities';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class NonSerializedItemsService {
  public _clientInventory = inject(NonSerializedInventoryGRPCService);
  private lowStockRequestToken = 0;
  private dataRequestToken = 0;
  private activeContractName = signal<string>('');
  private notification = inject(ONotificationService);
  private translate = inject(TranslateService);
  intialized: WritableSignal<boolean> = signal(false);
  pending: WritableSignal<boolean> = signal(true);
  success: WritableSignal<boolean> = signal(false);
  gridPending: WritableSignal<boolean> = signal(false);
  lowStockPending: WritableSignal<boolean> = signal(false);
  lowStockIssueCount: WritableSignal<number> = signal(0);
  lowStockWarnings: WritableSignal<LowStockWarningModel[]> = signal([]);
  data: WritableSignal<NonSerializedInventoryModel[]> = signal([]);
  readonly selectedInventory: WritableSignal<NonSerializedInventoryModel[]> = signal([]);

  loadedColumns: string[] = [];

  constructor() {}

  public getListByContract(contractName): void {
    this.activeContractName.set(contractName || '');
    this.intialized.set(true);
    this.data.set([]);
    this.updateSelected([]);
    this.pending.set(true);
    this.success.set(false);
    this.loadLowStockWarnings(contractName);
    const requestToken = ++this.dataRequestToken;
    this._clientInventory.getListByContract(contractName).subscribe({
      next: (data) => {
        if (requestToken !== this.dataRequestToken) {
          return;
        }
        this.data.set(data);
        this.pending.set(false);
        this.success.set(true);
      },
      error: (error) => {
        if (requestToken !== this.dataRequestToken) {
          return;
        }
        console.error(error);
      },
      complete: () => {
        if (requestToken !== this.dataRequestToken) {
          return;
        }
        this.pending.set(false);
      },
    });
  }
  updateSelected(inventory: NonSerializedInventoryModel[]) {
    this.selectedInventory.set(inventory);
  }

  updateInventory(inventory: NonSerializedInventoryModel[]) {
    this.data.set(inventory);
  }

  mergeInventoryByName(updatedInventories: NonSerializedInventoryModel[]) {
    if (!updatedInventories || updatedInventories.length === 0) {
      return;
    }

    const updatesByName = new Map(
      updatedInventories.map((inventory) => [inventory.name, inventory])
    );

    const merged = this.data().map((existingInventory) =>
      updatesByName.get(existingInventory.name) || existingInventory
    );

    for (const updatedInventory of updatedInventories) {
      const exists = merged.some((item) => item.name === updatedInventory.name);
      if (!exists) {
        merged.push(updatedInventory);
      }
    }

    this.data.set(merged);

    const selected = this.selectedInventory();
    if (selected && selected.length > 0) {
      this.selectedInventory.set(
        selected.map((item) => updatesByName.get(item.name) || item)
      );
    }
  }

  clear() {
    this.updateInventory([]);
    this.updateSelected([]);
    this.lowStockWarnings.set([]);
    this.lowStockIssueCount.set(0);
    this.lowStockPending.set(false);
    this.activeContractName.set('');
    this.loadedColumns = [];
  }

  loadLowStockWarnings(contractName: string) {
    if (!contractName) {
      this.lowStockWarnings.set([]);
      this.lowStockIssueCount.set(0);
      this.lowStockPending.set(false);
      return;
    }

    const requestToken = ++this.lowStockRequestToken;
    this.lowStockPending.set(true);
    this.lowStockWarnings.set([]);
    this.lowStockIssueCount.set(0);
    this._clientInventory.getLowStockWarnings(contractName).subscribe({
      next: (response) => {
        if (requestToken !== this.lowStockRequestToken) {
          return;
        }
        this.lowStockWarnings.set(response.warnings || []);
        this.lowStockIssueCount.set(response.issueCount || 0);
        this.lowStockPending.set(false);
      },
      error: (error) => {
        if (requestToken !== this.lowStockRequestToken) {
          return;
        }
        console.error('Error loading low stock warnings', error);
        this.lowStockWarnings.set([]);
        this.lowStockIssueCount.set(0);
        this.lowStockPending.set(false);
      },
      complete: () => {
        if (requestToken !== this.lowStockRequestToken) {
          return;
        }
        this.lowStockPending.set(false);
      },
    });
  }

  create(paylaod:NonSerializedInventoryModel): Observable<NonSerializedInventoryModel> {
    return new Observable((observer) => {
      this._clientInventory.create(paylaod).subscribe({
        next: (invItem: NonSerializedInventoryModel) => {
          const list = [...this.data(), invItem];
          this.data.set(list);
          this.refreshLowStockWarnings(paylaod?.contractName);
          observer.next(invItem);
          this.notification.showSuccess(
            this.translate.instant('NON_SERIALIZED.NOTIFICATIONS.CREATE_SUCCESS')
          );
        },
        error: (error) => {
          console.error('Error creating item', error);
          observer.error(error);
        },
        complete: () => {
          observer.complete();
        },
      });
    })
  }

  adjust(
    itemName: string,
    locationName: string,
    adjustment: NonSerializedAdjustmentNoteModel
  ): Observable<NonSerializedInventoryModel> {
    return new Observable((observer) => {
      this._clientInventory.adjust(itemName, locationName, adjustment).subscribe({
        next: (invItem: NonSerializedInventoryModel) => {
          this.mergeInventoryByName([invItem]);
          this.refreshLowStockWarnings(this.resolveContractNameForItem(itemName));
          observer.next(invItem);

          this.notification.showSuccess(
            this.translate.instant('NON_SERIALIZED.NOTIFICATIONS.ADJUST_SUCCESS')
          );
        },
        error: (error) => {
          console.error('Error creating item', error);
          observer.error(error);
        },
        complete: () => {
          observer.complete();
        },
      });
    })
  }

  transfer(
    payload: NonSerializedInventoryTransferModel
  ): Observable<NonSerializedInventoryModel[]> {
    return new Observable((observer) => {
      this._clientInventory.transfer(payload).subscribe({
        next: (inventories: NonSerializedInventoryModel[]) => {
          this.refreshLowStockWarnings();
          observer.next(inventories); 
          this.notification.showSuccess(
            this.translate.instant('NON_SERIALIZED.TRANSFER.NOTIFICATIONS.SUCCESS')
          );
        },
        error: (error) => {
          console.error('Error transferring inventory items', error);
          observer.error(error);
        },
        complete: () => {
          observer.complete();
        },
      });
    });
  }

  private refreshLowStockWarnings(contractName?: string) {
    const resolvedContractName = contractName || this.activeContractName();
    if (!resolvedContractName) {
      return;
    }
    this.loadLowStockWarnings(resolvedContractName);
  }

  private resolveContractNameForItem(itemName: string): string {
    const item = this.data().find((inventory) => inventory.name === itemName);
    return item?.contractName || this.activeContractName();
  }
}
